import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Settings,
  Building2,
  Tag,
  Bell,
  Users,
  Database,
  Mail,
  Shield,
  Wrench
} from 'lucide-react';
import { useAuth } from '@context/AuthContext';

import SystemSettings from '@components/settings/SystemSettings';
import ModuleSettings from '@components/settings/ModuleSettings';
import BranchSettings from '@components/settings/BranchSettings';
import NotificationSettings from '@components/settings/NotificationSettings';

const SettingsPage = () => {
  const [activeTab, setActiveTab] = useState('modules');
  const { user } = useAuth();

  useEffect(() => {
    document.title = 'Settings - RDX Ticketing System';
  }, []);

  const tabs = [
    // {
    //     id: 'system',
    //     label: 'System',
    //     icon: Wrench,
    //     description: 'General system configuration'
    // },
    {
      id: 'modules',
      label: 'Modules',
      icon: Tag,
      description: 'Manage modules and categories'
    },
    {
      id: 'branches',
      label: 'Branches',
      icon: Building2,
      description: 'Branch locations and settings'
    },
    {
      id: 'notifications',
      label: 'Notifications',
      icon: Bell,
      description: 'Email and notification settings'
    }
    // {
    //     id: 'security',
    //     label: 'Security',
    //     icon: Shield,
    //     description: 'Security and access control'
    // }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.32,
        staggerChildren: 0.08
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 12 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.32 } }
  };

  // Inline style objects to match Dashboard visual language
  const styles = {
    wrapper: {
      display: 'flex',
      flexDirection: 'column',
      gap: '1.5rem'
    },
    hero: {
      borderRadius: '1rem',
      padding: '1.25rem',
      color: 'white',
      overflow: 'hidden',
      boxShadow: '0 20px 40px rgba(2,6,23,0.08)'
    },
    heroInner: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '1rem'
    },
    heroLeft: {
      flex: 1,
      minWidth: 0
    },
    heroTitle: {
      fontSize: '1.375rem',
      fontWeight: 700,
      margin: 0,
      color: 'white',
      lineHeight: 1.1
    },
    heroSub: {
      marginTop: '0.375rem',
      fontSize: '0.9375rem',
      color: 'rgba(255,255,255,0.9)'
    },
    heroRight: {
      textAlign: 'right',
      minWidth: '11rem'
    },
    dateText: {
      margin: 0,
      fontSize: '0.9375rem',
      color: 'rgba(255,255,255,0.9)'
    },
    metaText: {
      marginTop: '0.25rem',
      fontSize: '0.8125rem',
      color: 'rgba(255,255,255,0.85)',
      textTransform: 'capitalize'
    },
    cardWrapper: {
      backgroundColor: 'transparent'
    },
    tabsWrapper: {
      background: '#fff',
      borderRadius: '1rem',
      padding: '1.5rem',
      boxShadow: '0 8px 24px rgba(2,6,23,0.04)',
      border: '1px solid rgba(14,20,25,0.04)'
    },
    tabsList: {
      display: 'flex',
      gap: '0.5rem',
      marginBottom: '1.5rem',
      borderBottom: '1px solid rgba(14,20,25,0.04)',
      paddingBottom: '1rem'
    },
    tab: (isActive) => ({
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      padding: '0.75rem 1rem',
      borderRadius: '0.5rem',
      border: 'none',
      background: isActive ? 'var(--primary-50)' : 'transparent',
      color: isActive ? 'var(--primary-700)' : 'var(--gray-600)',
      fontSize: '0.875rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.2s'
    })
  };

  // gradient background for settings (indigo theme)
  const heroGradient = {
    background: 'linear-gradient(90deg, #6366f1, #4f46e5)'
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'system':
        return <SystemSettings />;
      case 'modules':
        return <ModuleSettings />;
      case 'branches':
        return <BranchSettings />;
      case 'notifications':
        return <NotificationSettings />;
      case 'security':
        return (
          <div className="bg-white rounded-xl shadow-sm border p-8 text-center">
            <Shield className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
                            Security Settings
            </h3>
            <p className="text-gray-600">
                            Security configuration features coming soon.
            </p>
          </div>
        );
      default:
        return <ModuleSettings />;
    }
  };

  return (
    <motion.div
      style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Settings Hero */}
      <motion.div variants={itemVariants}>
        <div style={{ ...styles.hero, ...heroGradient }}>
          <div style={styles.heroInner}>
            <div style={styles.heroLeft}>
              <h1 style={styles.heroTitle}>
                <Settings style={{ display: 'inline-block', width: '1.5rem', height: '1.5rem', marginRight: '0.5rem' }} />
                                System Settings
              </h1>
              <p style={styles.heroSub}>
                                Configure your system preferences and manage application settings.
              </p>
            </div>

            <div style={styles.heroRight} className="hidden-md">
              <p style={styles.dateText}>
                {new Date().toLocaleDateString('en-IN', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
              <p style={styles.metaText}>
                {user?.role ? `${user.role}` : '—'} {user?.department ? `• ${user.department}` : ''}
              </p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Settings Content */}
      <motion.div variants={itemVariants}>
        <div style={styles.tabsWrapper}>
          {/* Tabs */}
          <div style={styles.tabsList}>
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
                            
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  style={styles.tab(isActive)}
                >
                  <Icon style={{ width: 16, height: 16 }} />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>

          {/* Tab Content */}
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
          >
            {renderTabContent()}
          </motion.div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default SettingsPage;